<template>
  <div
    ref="bar"
    class="c-track-bar"
    :class="{ 'c-track-bar--no-thumb': noThumb }"
  >
    <div
      ref="wrapper"
      class="c-track-bar__wrapper"
    >
      <div
        class="c-track-bar__bar"
        :tabindex="isTrackbarFocusable ? TabIndex.ENABLED : TabIndex.DISABLED"
      >
        <div
          v-for="({ percent }, index) in adsCuePoints"
          :key="`cue-point-${index}`"
          :style="{ transform: `translate3d(${(percent / 100) * elementWidth}px, 0, 0)` }"
          class="c-track-bar__ad-cue-point"
        ></div>
        <div
          class="c-track-bar__progress"
          :style="offset"
        ></div>
      </div>
      <div
        class="c-track-bar__thumb"
        :tabindex="isThumbFocusable ? TabIndex.ENABLED : TabIndex.DISABLED"
        :aria-label="thumbAriaLabel"
        :style="offset"
      ></div>
    </div>
  </div>
</template>
<script>
  import throttle from 'lodash/throttle';
  import { MouseButtonEvent, TabIndex } from '@/shared/enums';

  const HORIZONTAL = 'horizontal';
  const VERTICAL = 'vertical';

  const isTouchSupported = 'ontouchstart' in document.documentElement;
  const MOUSE_EVENT = {
    MOVE: isTouchSupported ? 'touchmove' : 'mousemove',
    UP: isTouchSupported ? 'touchend' : 'mouseup',
    DOWN: isTouchSupported ? 'touchstart' : 'mousedown',
  };

  export default {
    name: 'UiTrackBar',
    enums: {
      TabIndex,
    },
    props: {
      value: Number,
      dragLimit: Number,
      allowDrag: {
        type: Boolean,
        default: true,
      },
      direction: {
        type: String,
        default: HORIZONTAL,
        validator(val) {
          return [HORIZONTAL, VERTICAL].includes(val);
        },
      },
      adsCuePoints: {
        type: Array,
        default: () => [],
      },
      focusable: {
        type: Boolean,
        default: true,
      },
      noThumb: {
        type: Boolean,
        default: false,
      },
      ariaLabel: String,
    },
    data() {
      return {
        isActive: false,
        elementWidth: 0,
        elementRect: {
          top: 0,
          left: 0,
        },
        RAF: undefined,
      };
    },
    computed: {
      offset() {
        return { transform: `translate3d(${(this.value / 100) * this.elementWidth}px, 0, 0)` };
      },
      thumbAriaLabel() {
        return this.ariaLabel || this.$t('accessibility.trackbar.thumb');
      },
      isThumbFocusable() {
        return this.focusable && !this.noThumb;
      },
      isTrackbarFocusable() {
        return this.focusable && this.noThumb;
      },
    },
    created() {
      this.updateElement = throttle(this.updateElement, 50);
    },
    mounted() {
      this.updateElement();
      window.addEventListener('resize', this.updateElement);
      this.$el.addEventListener(MOUSE_EVENT.DOWN, this.moveStart, { passive: false });
    },
    updated() {
      this.updateElement();
    },
    beforeDestroy() {
      this.updateElement.cancel();
      this.$el.removeEventListener(MOUSE_EVENT.DOWN, this.moveStart);
      window.removeEventListener(MOUSE_EVENT.MOVE, this.updateMove);
      window.removeEventListener(MOUSE_EVENT.UP, this.moveEnd);
      window.removeEventListener('resize', this.updateElement);
    },
    methods: {
      setElementWidth() {
        this.elementWidth = this.$refs.wrapper.offsetWidth;
      },
      setElementRect() {
        this.elementRect = this.$refs.wrapper.getBoundingClientRect();
      },
      moveStart(e) {
        e.preventDefault();
        if (e.button !== MouseButtonEvent.LEFT && !isTouchSupported) {
          return false;
        }
        this.updateMove(e);
        if (this.allowDrag) {
          this.isActive = true;
          window.addEventListener(MOUSE_EVENT.UP, this.moveEnd);
          window.addEventListener(MOUSE_EVENT.MOVE, this.updateMove);
        }
        e.stopPropagation();
      },
      moveEnd() {
        this.isActive = false;
        window.removeEventListener(MOUSE_EVENT.MOVE, this.updateMove);
        window.removeEventListener(MOUSE_EVENT.UP, this.moveEnd);
      },
      updateMove(e) {
        if (this.RAF) {
          window.cancelAnimationFrame(this.RAF);
        }
        this.RAF = window.requestAnimationFrame(() => {
          e.stopPropagation();
          if (e.button !== MouseButtonEvent.LEFT && !isTouchSupported) {
            return false;
          }
          if (this.isActive) {
            this.$emit('changed', this.calculate(e));
          }
        });
      },
      calculate(e) {
        const position = this.getPosition(e);
        if (this.dragLimit && position > this.dragLimit) {
          return this.dragLimit;
        }
        return position;
      },
      getPosition(e) {
        if (this.direction === HORIZONTAL) {
          const clientX = e.clientX || e.touches[0].clientX;
          return this.getHorizontalPosition(clientX);
        }
        const clientY = e.clientY || e.touches[0].clientY;
        return this.getVerticalPosition(clientY);
      },
      getHorizontalPosition(clientX) {
        const offset = clientX - this.elementRect.left;
        if (clientX <= this.elementRect.left) {
          return 0;
        }
        if (clientX >= (this.elementRect.left + this.elementWidth)) {
          return 100;
        }
        return (offset / this.elementWidth) * 100;
      },
      getVerticalPosition(clientY) {
        const offset = clientY - this.elementRect.top;
        if (clientY <= this.elementRect.top) {
          return 0;
        }
        if (clientY >= (this.elementRect.top + this.elementWidth)) {
          return 100;
        }
        return (offset / this.elementWidth) * 100;
      },
      updateElement() {
        this.setElementWidth();
        this.setElementRect();
      },
    },
  };
</script>
